import React, { Fragment, useEffect, useRef, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

/* Firefox scrollbar */
const scrollbarStyle = {
  scrollbarWidth: "thin",
  scrollbarColor: "#9ca3af #f1f3f5",
};


import {
  // Column,
  // Table as ReactTable,
  // ColumnFiltersState,
  // FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from "components/Common/GlobalSearchFilter";

// Column Filter
const Filter = ({
  column
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '')}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainerTelecallerPerformance = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  onPageChange,
  onPageSizeChange,
  pageSize,
  setPageSize,
  totalPages,
  totalItems,
  tableNextPage,
  tablePreviousPage,
  tableCurrentPage,
  customePageSize,
  getRowProps,
  style,
  totalsOfColumn,
  totalleads,
  totalWorkOrder,  
  totalLost,
  totalConversion,
  telecallerPendingCalls,
}) => {

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pageIndex, setPageIndex] = useState(0); // Track current page index
  // console.log(pageIndex)

  // generate table-scoped unique ID
  const uid = useRef(`tbl-${Math.random().toString(36).substr(2, 9)}`).current;

  // dynamic ID names
  const topID = `topScroller-${uid}`;
  const bottomID = `bottomScroller-${uid}`;
  const topContentID = `topScrollContent-${uid}`;

  // Handle page change
  const handlePageChange = (newPageIndex) => {
    if (newPageIndex < 0 || newPageIndex >= totalPages) return; // Prevent invalid page navigation

    // console.log("Navigating to Page:", newPageIndex + 1);
    setPageIndex(newPageIndex);

    if (onPageChange) {
      onPageChange(newPageIndex + 1, pageSize); // Fetch the correct data from API
    }
  };


  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    // console.log("New Page Size Selected:", newPageSize);

    setPageSize(newPageSize);
    setPageIndex(0); // Reset to first page when page size changes

    if (onPageSizeChange) {
      onPageSizeChange(1, newPageSize); // Ensure the API fetches the correct records
    }
  };



  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    // setPageIndex,
    nextPage,
    previousPage,
    // setPageSize,
    getState
  } = table;

  // useEffect(() => {
  //   Number(customPageSize) && setPageSize(Number(customPageSize));
  // }, [customPageSize, setPageSize]);

  // useEffect(() => {
  //   console.log("Current Page Size:", pageSize);
  //   console.log("Current Page Index:", pageIndex);
  //   console.log("Total Rows in Data:", data.length);
  //   console.log("Rows Rendered in Table:", getRowModel().rows.length);
  // }, [pageSize, pageIndex, data]);

  // console.log(totalsOfColumn)

  const [rowHeight, setRowHeight] = useState(0);

  useEffect(() => {
    if (!isPagination) {
      // find first table row height dynamically
      const firstRow = document.querySelector(".custom-table tbody tr");
      if (firstRow) {
        setRowHeight(firstRow.getBoundingClientRect().height);
      }
    }
  }, [data, isPagination]);

  // ⭐ Sync scroll for this instance only
  useEffect(() => {
    const top = document.getElementById(topID);
    const bottom = document.getElementById(bottomID);

    if (!top || !bottom) return;

    const syncTop = () => (top.scrollLeft = bottom.scrollLeft);
    const syncBottom = () => (bottom.scrollLeft = top.scrollLeft);

    bottom.addEventListener("scroll", syncTop);
    top.addEventListener("scroll", syncBottom);

    return () => {
      bottom.removeEventListener("scroll", syncTop);
      top.removeEventListener("scroll", syncBottom);
    };
  }, [topID, bottomID]);

  // ⭐ Set top scrollbar width equal to table width
  useEffect(() => {
    const topContent = document.getElementById(topContentID);
    const bottom = document.getElementById(bottomID);

    if (topContent && bottom) {
      topContent.style.width = bottom.scrollWidth + "px";
    }
  }, [getRowModel().rows.length]);


  return (
    <Fragment>

      <Row className="mb-2">
        {isCustomPageSize && (
          <Col sm={2}>
            <select
              className="form-select pageSize mb-2"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {customePageSize.map((size, index) => (
                <option key={index} value={size}>
                  Show {size}
                </option>
              ))}
            </select>

          </Col>
        )}

        {isGlobalFilter && <DebouncedInput
          value={globalFilter ?? ''}
          onChange={value => setGlobalFilter(String(value))}
          className="form-control search-box me-2 mb-2 d-inline-block"
          placeholder={SearchPlaceholder}
        />}

        {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}

        {isAddButton && <Col sm={6}>
          <div className="text-sm-end">
            <Button type="button" className={buttonClass} onClick={handleUserClick}>
              <i className="mdi mdi-plus me-1"></i> {buttonName}</Button>
          </div>
        </Col>}
      </Row>

      <style>{`
        #${topID}::-webkit-scrollbar,
        #${bottomID}::-webkit-scrollbar {
          height: 8px;
          background: #f1f3f5;
        }
        #${topID}::-webkit-scrollbar-thumb,
        #${bottomID}::-webkit-scrollbar-thumb {
          background: #9ca3af;
          border-radius: 10px;
        }
        #${topID}::-webkit-scrollbar-thumb:hover,
        #${bottomID}::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>

      {/* ⭐ TOP SCROLLER (unique per table) */}
      <div
        id={topID}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          height: "14px",
          ...scrollbarStyle,
        }}
      >
        <div id={topContentID}></div>
      </div>

      {/* ⭐ TABLE SCROLLER */}
      <div
        id={bottomID}
        className={divClassName || "table-responsive custom-table"}
        style={{
          ...style,
          maxHeight: !isPagination && rowHeight ? `${rowHeight * 21}px` : "unset",
          overflowX: "auto",
          overflowY: "auto",
          ...scrollbarStyle,
        }}
      >
        <Table className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {totalsOfColumn && Object.keys(totalsOfColumn).length > 0 && (
              <tr style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                <th colSpan={0}>Total</th>
                <th>{''}</th>
                <th>{totalleads ?? 0}</th>
                <th>{totalWorkOrder ?? 0}</th>                
                <th>{totalLost ?? 0}</th>
                <th>{totalConversion ?? 0}</th>
                <th>{''}</th>
                <th>{''}</th>
                <th>{''}</th>
                <th>{'Live Pending Calls - ' + ( telecallerPendingCalls ?? 0)}</th>
                
              </tr>
            )}

            {/* {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))} */}

            {getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isSortable = header.column.getCanSort();

                return (
                  <th
                    key={header.id}
                    onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                    style={{
                      cursor: isSortable ? "pointer" : "default",
                      userSelect: "none"
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}

                    {isSortable && (
                      <span className="ms-1">
                        {{
                          asc: "🔼",
                          desc: "🔽"
                        }[header.column.getIsSorted()] ?? ""}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}

          </thead>

          {/* <tbody>
            {getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody> */}

          <tbody>
            {getRowModel().rows.map(row => {
              const rowProps = getRowProps ? getRowProps(row) : {};

              return (
                <tr key={row.id} {...rowProps}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>

        </Table>
      </div>


      {
        isPagination && (
          <Row className="align-items-center">
            <Col sm={12} md={5}>
              <div className="dataTables_info text-center text-md-start">
                Showing {data.length} of {totalItems} Results
              </div>
            </Col>
            <Col sm={12} md={7}>
              <div className="pagination-wrapper d-flex justify-content-center justify-content-md-end">
                <ul className="pagination d-flex flex-wrap overflow-auto">
                  {/* Previous Page Button */}
                  <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(pageIndex - 1)} disabled={pageIndex === 0}>
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                  </li>

                  {/* Page Numbers - Show fewer numbers on small screens */}
                  {totalPages > 5 ? (
                    <>
                      {pageIndex > 2 && <li className="page-item"><span className="page-link">...</span></li>}
                      {Array.from({ length: totalPages }, (_, index) => {
                        if (index === 0 || index === totalPages - 1 || (index >= pageIndex - 1 && index <= pageIndex + 1)) {
                          return (
                            <li key={index} className={`page-item ${pageIndex === index ? "active" : ""}`}>
                              <button className="page-link" onClick={() => handlePageChange(index)}>
                                {index + 1}
                              </button>
                            </li>
                          );
                        }
                        return null;
                      })}
                      {pageIndex < totalPages - 3 && <li className="page-item"><span className="page-link">...</span></li>}
                    </>
                  ) : (
                    Array.from({ length: totalPages }, (_, index) => (
                      <li key={index} className={`page-item ${pageIndex === index ? "active" : ""}`}>
                        <button className="page-link" onClick={() => handlePageChange(index)}>
                          {index + 1}
                        </button>
                      </li>
                    ))
                  )}

                  {/* Next Page Button */}
                  <li className={`page-item ${pageIndex >= totalPages - 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(pageIndex + 1)} disabled={pageIndex >= totalPages - 1}>
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        )
      }


      {/* {isPagination && (
        <Row className="align-items-center">
          <Col sm={12} md={5}>
            <div className="dataTables_info">
              Showing {data.length} of {totalItems} Results
            </div>
          </Col>
          <Col sm={12} md={7}>
            <div className="pagination-wrapper d-flex justify-content-end">
              <ul className="pagination">
              
                <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(pageIndex - 1)} disabled={pageIndex === 0}>
                    <i className="mdi mdi-chevron-left"></i>
                  </button>
                </li>

               
                {Array.from({ length: totalPages }, (_, index) => (
                  <li key={index} className={`page-item ${pageIndex === index ? "active" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(index)}>
                      {index + 1}
                    </button>
                  </li>
                ))}

                
                <li className={`page-item ${pageIndex >= totalPages - 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => handlePageChange(pageIndex + 1)} disabled={pageIndex >= totalPages - 1}>
                    <i className="mdi mdi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )} */}

    </Fragment >
  );
};

export default TableContainerTelecallerPerformance;